/**
 * DeviceEnums Component
 * 
 * Main component for managing device enumerations (device types/models).
 * Displays a list of all device enums with the ability to:
 * - Add new device types
 * - View existing devices in a grid layout
 * - Edit or delete device entries via the card actions
 * 
 * The component uses Firestore real-time listener to keep the device list
 * synchronized with the database.
 * 
 * Layout adapts based on device width:
 * - Desktop (>800px): 2-column grid
 * - Mobile (<=800px): Single column
 */
import React, { useEffect, useState } from "react";

import {
  onSnapshot,
  collection,
  query,
} from "firebase/firestore";
import CircularProgress from "@mui/material/CircularProgress";

import { firebaseStore } from "../../../components/firebase/firestore";
import { useDeviceState } from "../../../components/utils/globalStates";
import AddDeviceEnum from "./components/AddDeviceEnum";
import CardsDeviceEnumList from "../../../components/ui/cards/CardsDeviceEnumList";

import "./DeviceEnums.css";

function DeviceEnums() {
  const deviceState = useDeviceState();
  
  // State for storing the array of device enums from Firestore
  const [deviceEnums, setDeviceEnums] = useState<any[]>([]);
  
  // Loading state for initial data fetch
  const [searchLoading, setSearchLoading] = useState(true);

  /**
   * Set up Firestore real-time listener for deviceEnums collection
   * Updates component state whenever data changes in Firestore
   * Automatically unsubscribes on component unmount
   */
  useEffect(() => {
    const q = query(collection(firebaseStore, "deviceEnums"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const tempDeviceEnums: any[] = [];
      querySnapshot.forEach((doc) => {
        // Combine document data with its ID for reference
        tempDeviceEnums.push({...doc.data(), id: doc.id});
      });
      setDeviceEnums(tempDeviceEnums);
      setSearchLoading(false);
    });
    
    // Cleanup: Unsubscribe from listener when component unmounts
    return unsubscribe;
  }, []);

  return (
    <div className="DeviceEnums">
      {/* Add New Device Enum Button/Dialog */}
      <AddDeviceEnum />
      
      {/* Loading State: Show spinner while fetching data */}
      {searchLoading ? (
        <div className="DeviceEnumsSearchLoading">
          <CircularProgress color="inherit" />
        </div>
      ) : (
        /* Device Enums Grid: Responsive layout that adapts to screen size */
        <div className="DeviceEnumsList">
          {deviceEnums.map((device, index) => (
            <CardsDeviceEnumList
              key={index}
              deviceEnum={device}
              isSmall={deviceState.deviceWidth > 800}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default DeviceEnums;